import { useEffect, useMemo, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useSearchParameters } from '@/components/signup/hooks/use-search-parameters';
import { isJsmPlaygroundSignup } from '@/components/signup/utils/jsm-playground-signup-intent';
import { ProductKey } from '@/components/signup/utils/products';
import { useUserContext } from '@/components/user-context/user-context-provider';
import { DataFoundationsAttributes } from '@/templates/ondemand/signup/confirmation/confirmation-page/src/views/user-seg-bxp-ui/types.ts';
import { DataFoundationsWrapper } from '@/utils/data-foundations/data-foundation-types';
import { experiments } from '@/utils/experiment/manifest';
import { useExperiment } from '@/utils/experiment/use-experiment';
import { useExperimentOnView } from '@/utils/experiment/use-experiment-on-view';

import { useConfirmationPage } from '../../hooks/use-confirmation-page';
import { useScreenManager } from '../../hooks/use-screen-manager';
import { getPrimaryProduct } from '../../hooks/utils/use-cloud-status-utils';
import {
	USER_SEGMENTATION_TEAM_TYPES,
	USER_SEGMENTATION_TEAM_TYPE_CUSTOMER_SERVICE,
	UserSegmentationTeamTypes,
} from '../../models/user-segmentation';
import { getCurrentUrlWithParams } from '../../utils/utils';

import { ATLAS_TICKET, TRACKING_EVENT_ACTION_SUBJECT_IDS, USER_SEARCH_PARAMS } from './constants';
import TeamFocusView from './team-focus-view';

const ViewManager = () => {
	const { nextScreen } = useScreenManager();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const {
		availableProducts,
		userSegmentationContext: { closeUserSegmentationUI, setSelectedTeamType },
	} = useConfirmationPage();

	const { paramsTeamType, isPlaygroundSignup } = useMemo(() => {
		const searchParameters = new URLSearchParams(window.location.search);

		return {
			paramsTeamType: (searchParameters.get(USER_SEARCH_PARAMS.teamType) ??
				'') as UserSegmentationTeamTypes,
			isPlaygroundSignup: isJsmPlaygroundSignup(searchParameters),
		};
	}, []);

	const { products, edition } = useSearchParameters();
	const productKey = getPrimaryProduct(products);
	const isServiceCollectionFlow = products.includes(ProductKey.SERVICE_COLLECTION);
	const jsmUseCaseExperimentOnView = useExperimentOnView(
		experiments.jsmGrowth519JsmLandUseCaseOnboarding,
		{
			metadata: {
				source: 'TeamChoiceScreen',
			},
		},
	);
	const isJsmUseCaseExperimentEligible = !availableProducts?.sites?.some((site) =>
		site.products.includes(ProductKey.JIRA_SERVICE_MANAGEMENT),
	);
	const jsmGrowth519Result = useExperiment(experiments.jsmGrowth519JsmLandUseCaseOnboarding);
	const isJsmUseCaseEnabled =
		jsmGrowth519Result.type === 'enrolled'
			? jsmGrowth519Result.value
			: experiments.jsmGrowth519JsmLandUseCaseOnboarding.defaultValue;
	const jsmUseCaseCohort = isJsmUseCaseEnabled ? 'test' : 'control';
	const { profile } = useUserContext();
	const userType = profile.cohorts?.userType;
	const domainType = profile.cohorts?.domainType;
	const companyType = profile.cohorts?.companyType;
	const [waitForAttributes, setWaitForAttributes] = useState(true);
	const [dataFoundationAttributes, setDataFoundationAttributes] =
		useState<DataFoundationsAttributes>({
			productKey,
			edition,
		});

	useEffect(() => {
		if (!profile.requestInProgress) {
			setDataFoundationAttributes((prevAttributes) => ({
				...prevAttributes,
				userType: userType,
				domainType: domainType,
				companyType: companyType,
				jsmUseCaseCohort: jsmUseCaseCohort,
			}));
			setWaitForAttributes(false);
		}
	}, [profile.requestInProgress, userType, domainType, companyType, jsmUseCaseCohort]);

	useEffect(() => {
		if (isPlaygroundSignup && USER_SEGMENTATION_TEAM_TYPES.includes(paramsTeamType)) {
			setSelectedTeamType(paramsTeamType);
			return;
		}

		// If team type is set as a query parameter, the user has refreshed the page.
		// Add the selected team type to state and close the user segmentation UI.
		if (USER_SEGMENTATION_TEAM_TYPES.includes(paramsTeamType)) {
			setSelectedTeamType(paramsTeamType);

			const isCustomerServiceSelected =
				paramsTeamType === USER_SEGMENTATION_TEAM_TYPE_CUSTOMER_SERVICE;
			const shouldRouteCustomerServiceToJcs = isServiceCollectionFlow && isCustomerServiceSelected;

			if (shouldRouteCustomerServiceToJcs) {
				const currentUrl = new URL(window.location.href);
				if (!currentUrl.searchParams.get('primaryCollectionProduct')) {
					const updatedUrl = getCurrentUrlWithParams({
						primaryCollectionProduct: ProductKey.JIRA_CUSTOMER_SERVICE,
					});
					history.replaceState({}, '', updatedUrl);
				}
			}

			closeUserSegmentationUI();
			nextScreen();
			return;
		}

		createAnalyticsEvent({
			action: 'viewed',
			eventType: 'screen',
			name: TRACKING_EVENT_ACTION_SUBJECT_IDS.userSegRedesignScreen,
			attributes: {
				atlasTicket: ATLAS_TICKET,
			},
		}).fire();
	}, [isPlaygroundSignup, paramsTeamType]);

	return USER_SEGMENTATION_TEAM_TYPES.includes(paramsTeamType) && !isPlaygroundSignup ? null : (
		<div
			ref={isJsmUseCaseExperimentEligible ? jsmUseCaseExperimentOnView.inViewHook.ref : undefined}
		>
			<DataFoundationsWrapper
				dataFoundationsEventOption="TeamChoiceScreenViewed"
				attributes={dataFoundationAttributes}
				waitForAttributes={waitForAttributes}
			>
				<TeamFocusView />
			</DataFoundationsWrapper>
		</div>
	);
};

export default ViewManager;
